import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Check } from '@phosphor-icons/react';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemesForPersona, getThemeTokens, AppTheme } from '@/utils/appThemes';
import { contrastOn } from '@/utils/colorMath';
import { useActivePersona } from '@/personas/useActivePersona';
import { SettingsConfig } from './SettingsAccordionConfig';
import { useRef, useEffect } from 'react';

const ThemeSelector = (_props: SettingsConfig) => {
  const { t } = useTranslation();
  const { selectedTheme, setSelectedTheme, isDarkMode } = useThemePreference();
  const profile = useActivePersona();
  const themes = getThemesForPersona(profile.persona);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll active theme into view on mount
  useEffect(() => {
    if (!scrollRef.current) return;
    const activeEl = scrollRef.current.querySelector('[data-active="true"]');
    if (activeEl) {
      activeEl.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
    // selectedTheme är redan persona-upplöst av ThemePreferenceProvider — samma värde
    // som appen faktiskt målar med, så den prick som markeras är den som gäller.
  }, [selectedTheme]);

  return (
    <Box sx={{ p: 2, width: '100%' }}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 2, fontWeight: 600, color: 'text.primary', fontSize: '0.85rem' }}
      >
        {t('settings.theme.title')}
      </Typography>

      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          gap: 1.5,
          overflowX: 'auto',
          overflowY: 'hidden',
          pb: 1,
          scrollPaddingInline: '16px',
          scrollSnapType: 'x proximity',
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.15)',
            borderRadius: 2,
          },
        }}
        role="radiogroup"
        aria-label={t('settings.theme.title')}
      >
        {themes.map((theme: AppTheme) => {
          const isActive = selectedTheme === theme.id;
          // Pricken ska visa temat som det faktiskt kommer att se ut — i mörkt
          // läge är det dark.primary, inte light.primary.
          const swatch = getThemeTokens(theme.id, isDarkMode).primary;

          return (
            <Box
              key={theme.id}
              data-active={isActive}
              component="button"
              type="button"
              role="radio"
              aria-checked={isActive}
              aria-label={t(theme.name)}
              onClick={() => setSelectedTheme(theme.id)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.75,
                p: 1,
                border: 'none',
                bgcolor: 'transparent',
                cursor: 'pointer',
                fontFamily: 'inherit',
                flexShrink: 0,
                scrollSnapAlign: 'center',
                transition: 'transform 0.15s ease',
                '@media (hover: hover)': {
                  '&:hover': { transform: 'scale(1.05)' },
                },
                '&:active': { transform: 'scale(0.95)' },
                '&:focus-visible': {
                  outline: `2px solid ${swatch}`,
                  outlineOffset: 4,
                  borderRadius: '50%',
                },
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              }}
            >
              {/* Color circle */}
              <Box
                sx={{
                  position: 'relative',
                  width: isActive ? 56 : 48,
                  height: isActive ? 56 : 48,
                  borderRadius: '50%',
                  bgcolor: swatch,
                  border: isActive ? '3px solid' : '2px solid',
                  borderColor: isActive ? swatch : 'divider',
                  boxShadow: isActive ? `0 0 0 3px ${swatch}33, 0 4px 12px ${swatch}22` : 'none',
                  transition: 'all 0.2s ease',
                  display: 'grid',
                  placeItems: 'center',
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                }}
              >
                {isActive && <Check size={20} weight="bold" color={contrastOn(swatch)} />}
              </Box>

              {/* Theme name */}
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? swatch : 'text.secondary',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  maxWidth: 64,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {t(theme.name)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ThemeSelector;
